import React, { useState } from "react";
import { CreditCard } from "lucide-react";
import toast from "react-hot-toast";
import { useFiatOnramp, usePrivy } from "@privy-io/react-auth";
import { useQuery } from "@tanstack/react-query";
import { meService } from "../../services/me.service";
import { usePrivyAvailable } from "../../context/PrivyProviderGate";

function PrivyCardOnrampAction({ transferId, configuration }) {
  const { fund } = useFiatOnramp();
  const { ready, authenticated, login, getAccessToken } = usePrivy();
  const [isOpening, setIsOpening] = useState(false);

  async function openCardOnramp() {
    setIsOpening(true);
    try {
      // createOnLogin is 'off', so a BluPay session does not imply an
      // authenticated Privy session yet — establish one on demand.
      if (!authenticated) {
        await login();
      }

      // Belt-and-braces check: `authenticated` can read true from a stale
      // session, and `login()` resolving doesn't guarantee the resulting
      // token has fully propagated. Confirm a real, usable token exists
      // before opening the funding modal — this is what "Missing auth
      // token" from the onramp API means when it happens.
      const token = await getAccessToken();
      if (!token) {
        toast.error("Your session isn't ready yet. Please try again.");
        return;
      }

      const result = await fund({
        source: {
          assets: configuration.source_assets,
          defaultAsset: configuration.default_asset,
        },
        // Always the fixed BluPay settlement wallet — never a per-user wallet.
        destination: configuration.destination,
        environment: configuration.environment,
        defaultAmount: configuration.default_amount,
      });
      const rawStatus = typeof result === "string" ? result : result?.status;
      const status = rawStatus === "confirmed" ? "confirmed" : "submitted";
      await meService.recordCardOnrampSubmission(transferId, status);
      toast.success(
        status === "confirmed"
          ? "Card purchase confirmed. We are verifying the deposit."
          : "Card purchase submitted. We will continue after the deposit arrives."
      );
    } catch (error) {
      if (error?.message && !/cancel/i.test(error.message)) {
        toast.error("The card funding flow could not be completed.");
      }
    } finally {
      setIsOpening(false);
    }
  }

  const disabled = isOpening || !ready;

  return (
    <div className="rounded-2xl border border-[#0A4730]/10 bg-white/80 p-4 text-left shadow-sm">
      <button
        type="button"
        onClick={openCardOnramp}
        disabled={disabled}
        className="w-full rounded-xl bg-[#0A4730] px-4 py-3 font-semibold text-white disabled:opacity-50 flex items-center justify-center gap-2"
      >
        <CreditCard size={18} />
        {!ready
          ? "Loading..."
          : isOpening
          ? "Opening secure checkout..."
          : "Fund with card"}
      </button>
      <p className="mt-2 text-xs leading-relaxed text-[#0A4730]/60">
        Card details are handled by Privy and its onramp provider. Your transfer
        continues only after BluPay independently verifies the deposit.
      </p>
    </div>
  );
}

export function PrivyCardOnrampButton({ transferId }) {
  const privyAvailable = usePrivyAvailable();

  const query = useQuery({
    queryKey: ["me", "transfers", transferId, "card-onramp"],
    queryFn: () => meService.getCardOnramp(transferId),
    enabled: Boolean(privyAvailable && transferId),
    retry: false,
  });

  if (!privyAvailable || query.isLoading || !query.data) {
    return null;
  }

  return (
    <PrivyCardOnrampAction
      transferId={transferId}
      configuration={query.data}
    />
  );
}